import { useCallback, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/Button";

/**
 * P4 — invitation discrète à installer le portail en PWA.
 *
 * Composant AUTONOME : il est monté une seule fois depuis `main.tsx`, à côté de
 * `<App />`, et se place lui-même hors du routeur. Il n'a donc besoin d'aucune
 * modification des pages existantes.
 *
 * Règles d'apparition — la bannière ne s'affiche QUE si tout est vrai :
 *   1. le navigateur a émis `beforeinstallprompt` (donc installation réellement
 *      possible : ni déjà installée, ni iOS Safari qui n'implémente pas l'API) ;
 *   2. l'application ne tourne pas déjà en mode autonome ;
 *   3. l'utilisateur est sur le tableau de bord — pas sur l'écran de connexion, où
 *      proposer une installation avant même de savoir se connecter n'a aucun sens ;
 *   4. l'invitation n'a pas été refusée dans les 30 derniers jours.
 */

/**
 * Refus mémorisé. Clé volontairement HORS des préfixes purgés par
 * `purgePrivateCaches()` : c'est une préférence d'affichage sans aucune donnée
 * personnelle, elle doit survivre à une déconnexion.
 */
const DISMISS_KEY = "anto_install_prompt_dismissed_at";
const DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** Route sur laquelle l'invitation est proposée. */
const PROMPT_PATH = "/dashboard";

/** Événement maison : react-router navigue par pushState, qui n'émet pas `popstate`. */
const LOCATION_EVENT = "portal:locationchange";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let historyPatched = false;

/**
 * Fait émettre `portal:locationchange` à chaque navigation programmée. Patch posé
 * une seule fois, idempotent, et sans effet sur le comportement de l'History API
 * (la valeur de retour d'origine est renvoyée telle quelle).
 */
function patchHistory(): void {
  if (historyPatched) return;
  historyPatched = true;
  const pushState = history.pushState.bind(history);
  const replaceState = history.replaceState.bind(history);
  history.pushState = (...args: Parameters<History["pushState"]>) => {
    pushState(...args);
    window.dispatchEvent(new Event(LOCATION_EVENT));
  };
  history.replaceState = (...args: Parameters<History["replaceState"]>) => {
    replaceState(...args);
    window.dispatchEvent(new Event(LOCATION_EVENT));
  };
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    // Valeur illisible (stockage bidouillé) : on considère le refus toujours valable
    // plutôt que de re-solliciter le client en boucle.
    if (!Number.isFinite(at)) return true;
    return Date.now() - at < DISMISS_WINDOW_MS;
  } catch {
    return true;
  }
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [onPromptPath, setOnPromptPath] = useState(
    () => window.location.pathname === PROMPT_PATH,
  );
  const [dismissed, setDismissed] = useState(isDismissed);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Sans preventDefault(), Chrome affiche sa propre mini-infobar : on garde la
      // main pour ne solliciter le client qu'au bon moment.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    patchHistory();
    const sync = () => setOnPromptPath(window.location.pathname === PROMPT_PATH);
    window.addEventListener(LOCATION_EVENT, sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener(LOCATION_EVENT, sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* stockage indisponible — le refus vaut au moins pour cette session */
    }
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    // L'événement n'est utilisable qu'une fois : qu'il ait accepté ou refusé, on
    // retire la bannière et on n'y revient pas avant 30 jours.
    setDeferred(null);
    dismiss();
  }, [deferred, dismiss]);

  if (!deferred || dismissed || !onPromptPath || isStandalone()) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-3xl items-center gap-3 rounded-xl2 border border-border bg-card p-3 shadow-lg">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl2 bg-terracotta/10 text-terracotta">
          <Download size={20} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Installer votre espace client</p>
          <p className="text-xs text-muted">
            Accès direct depuis votre écran d'accueil, sans passer par le navigateur.
          </p>
        </div>
        <Button size="sm" onClick={install}>
          Installer
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Ne plus proposer l'installation"
          className="shrink-0 rounded-xl2 p-2 text-muted transition-colors hover:bg-border/30 hover:text-ink"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}
